<template>
  <div class="rich-editor">
    <textarea :id="editorId"></textarea>
  </div>
</template>

<script>
import tinymce from 'tinymce/tinymce'
import 'tinymce/icons/default'
import 'tinymce/themes/silver'
import 'tinymce/models/dom'
import 'tinymce/plugins/advlist'
import 'tinymce/plugins/autolink'
import 'tinymce/plugins/lists'
import 'tinymce/plugins/link'
import 'tinymce/plugins/image'
import 'tinymce/plugins/media'
import 'tinymce/plugins/table'
import 'tinymce/plugins/charmap'
import 'tinymce/plugins/preview'
import 'tinymce/plugins/anchor'
import 'tinymce/plugins/searchreplace'
import 'tinymce/plugins/visualblocks'
import 'tinymce/plugins/code'
import 'tinymce/plugins/fullscreen'
import 'tinymce/plugins/insertdatetime'
import 'tinymce/plugins/wordcount'
import 'tinymce/skins/ui/oxide/skin.css'
import 'tinymce/skins/ui/oxide/content.css'
import 'tinymce/skins/content/default/content.css'
import api from '@/utils/api'

export default {
  name: 'RichEditor',
  props: {
    value: {
      type: String,
      default: ''
    },
    height: {
      type: Number,
      default: 360
    },
    placeholder: {
      type: String,
      default: '请输入内容'
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      editorId: `rich-editor-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      editor: null,
      editorReady: false,
      pendingValue: '',
      isApplyingValue: false,
      isSettingValue: false
    }
  },
  computed: {
    imageDomain() {
      return this.$store.getters.getImg || ''
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (this.isSettingValue) {
          return
        }
        this.applyValue(newVal)
      },
      immediate: true
    },
    disabled(newVal) {
      this.setReadonly(newVal)
    }
  },
  mounted() {
    this.initEditor()
  },
  beforeDestroy() {
    if (this.editor) {
      tinymce.remove(this.editor)
      this.editor = null
    }
    this.editorReady = false
  },
  methods: {
    initEditor() {
      this.createEditor()
    },
    createEditor() {
      const oldEditor = tinymce.get(this.editorId)
      if (oldEditor) {
        tinymce.remove(oldEditor)
      }

      tinymce.init({
        selector: `#${this.editorId}`,
        height: this.height,
        branding: false,
        menubar: false,
        statusbar: true,
        resize: true,
        readonly: this.disabled,
        placeholder: this.placeholder,
        skin: false,
        content_css: false,
        font_size_formats: '12px 14px 16px 18px 20px 24px 28px 32px 36px',
        plugins: 'advlist autolink lists link image media table charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime wordcount',
        toolbar: [
          'undo redo | blocks fontsize | bold italic underline strikethrough forecolor backcolor',
          'alignleft aligncenter alignright alignjustify | bullist numlist outdent indent',
          'link uploadimage image media table | removeformat code fullscreen preview'
        ].join(' | '),
        automatic_uploads: true,
        paste_data_images: true,
        file_picker_types: 'image',
        images_upload_handler: this.uploadEditorImage,
        file_picker_callback: this.pickLocalImage,
        content_style: [
          'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif; font-size: 14px; }',
          'img { max-width: 100%; height: auto; }'
        ].join(' '),
        setup: (editor) => {
          this.editor = editor

          editor.ui.registry.addButton('uploadimage', {
            text: '上传图片',
            icon: 'image',
            tooltip: '上传图片',
            onAction: () => {
              this.pickLocalImage((url, meta = {}) => {
                editor.insertContent(`<img src="${url}" alt="${meta.title || ''}" />`)
              })
            }
          })

          editor.on('init', () => {
            this.editorReady = true
            this.applyValue(this.pendingValue || this.value || '', true)
            this.setReadonly(this.disabled)
          })

          editor.on('input change undo redo keyup paste', () => {
            if (this.isApplyingValue) {
              return
            }
            this.emitContent(editor.getContent())
          })
        }
      })
    },
    normalizeContent(value) {
      return value == null ? '' : String(value)
    },
    applyValue(value, force = false) {
      const content = this.normalizeContent(value)
      this.pendingValue = content

      if (!this.editor || !this.editorReady) {
        return
      }

      if (!force && this.editor.getContent() === content) {
        return
      }

      this.isApplyingValue = true
      this.editor.setContent(content)
      this.$nextTick(() => {
        this.isApplyingValue = false
      })
    },
    emitContent(content) {
      this.isSettingValue = true
      this.$emit('input', content)
      this.$emit('change', content)
      this.$nextTick(() => {
        this.isSettingValue = false
      })
    },
    setReadonly(disabled) {
      if (!this.editor || !this.editor.mode) {
        return
      }
      this.editor.mode.set(disabled ? 'readonly' : 'design')
    },
    pickLocalImage(callback) {
      const input = document.createElement('input')
      input.type = 'file'
      input.accept = 'image/jpeg,image/png,image/gif,image/webp'
      input.style.display = 'none'
      input.onchange = () => {
        const file = input.files && input.files[0]
        document.body.removeChild(input)
        if (!file) {
          return
        }

        this.uploadFile(file)
          .then((url) => callback(url, { title: file.name }))
          .catch(() => {})
      }
      document.body.appendChild(input)
      input.click()
    },
    uploadEditorImage(blobInfo) {
      return this.uploadFile(blobInfo.blob())
    },
    uploadFile(file) {
      const isImage = /^image\/(jpeg|png|gif|webp)$/.test(file.type)
      const isLt5M = file.size / 1024 / 1024 < 5

      if (!isImage) {
        this.$message.error('只能上传 JPG、PNG、GIF、WEBP 图片')
        return Promise.reject(new Error('Invalid image type'))
      }

      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB')
        return Promise.reject(new Error('Image is too large'))
      }

      return api.file.uploadImage(file).then((res) => {
        const relativePath = res.data && res.data.fileInfo && res.data.fileInfo.relativePath
        if (!relativePath) {
          throw new Error('上传接口未返回图片地址')
        }
        return this.normalizeImageUrl(relativePath)
      }).catch((error) => {
        this.$message.error('图片上传失败')
        throw error
      })
    },
    normalizeImageUrl(path) {
      if (/^https?:\/\//.test(path)) {
        return path
      }

      const domain = this.imageDomain.replace(/\/$/, '')
      const cleanPath = String(path).replace(/^\//, '')
      return domain ? `${domain}/${cleanPath}` : `/${cleanPath}`
    }
  }
}
</script>

<style scoped>
.rich-editor {
  width: 100%;
}
</style>

<style>
.tox-tinymce-aux,
.tox-dialog-wrap,
.tox-dialog-wrap__backdrop,
.tox-silver-sink {
  z-index: 3000 !important;
}
</style>
